"use client";

import Image from "next/image";
import styles from "./info-tabs.module.scss";
import TimeseriesChart from "./TimeseriesChart";

const VWBAnalysisTab = () => {
  return (
    <div className={styles.tabContent}>
      <h2>Volumetric Water Balance (VWB) Analysis</h2>

      <p>
        The <strong>Volumetric Water Balance (VWB)</strong> module in AI2Peat
        enables detailed time series analysis of <em>Water Table Depth (WTD)</em> and
        <em> Precipitation</em> across fixed dipwell locations. This is used to estimate
        <strong> Specific Yield (Sy)</strong>—a critical hydrological property
        influencing peatland water storage, flow regulation, and carbon balance.
      </p>

      <h3>What is Specific Yield (Sy)?</h3>
      <p>
        Specific Yield is defined as the amount of water released or stored in a peat
        profile per unit change in water table depth. It varies depending on depth,
        peat composition, and geomorphological history. Accurate estimation of Sy
        helps model water retention and predict peatland response to climate change.
      </p>

      <ul>
        <li>
          <strong>High Sy</strong>: Indicates gradual water table changes and high
          storage potential.
        </li>
        <li>
          <strong>Low Sy</strong>: Suggests rapid fluctuations and limited storage
          capacity.
        </li>
      </ul>
      <h3>Example: VWB Analysis of a Selected Region</h3>

      <p>
        In the example below, a rectangular boundary was selected around the
        <strong> Sharavogue Raised Bog</strong> in central Ireland, bounded
        approximately by coordinates: <code>53.331°N to 53.344°N</code> and{" "}
        <code>–7.358°W to –7.328°W</code>. This region includes known dipwell locations
        and allows detailed time series water balance analysis using daily rainfall
        and dipwell data.
      </p>

      <video
        className={styles.image}
        autoPlay        // start automatically
        muted           // required for autoplay on most browsers
        loop            // keep it running
        playsInline     // prevent iOS from going fullscreen
        preload="metadata"
        poster="/info-page/vwb.png"
      >
        <source src="/info-page/timeseries.mp4" type="video/mp4" />
        {/* Optional second source for broader support
        <source src="/info-page/ecotopemap.webm" type="video/webm" /> */}
      </video>

      <p>
        The selected area corresponds to a portion of the <strong>Living Bog Project</strong>, where
        long-term dipwell and rain data enabled specific yield estimation using conservative
        daily methods. This technique provides reliable estimates in areas lacking continuous
        hourly rainfall data.
      </p>

      <h3>Example: VWB Analysis from Sharavogue and Liffey Head</h3>

      <p>
        In the example below, WTD and rainfall are plotted for a site at Sharavogue.
        Specific Yield (Sy = 0.166) was derived using rainfall events and dipwell
        response. The chart shows seasonal variation in WTD and rainfall over time.
      </p>

      <TimeseriesChart
        endpoint="/api/vwb-statistics/"
        payload={{
          site_name: "mullingar",
          start_date: "2018-07-01",
          end_date: "2022-06-22",
        }}
      />

      <p>
        Results from other sites such as <strong>Liffey Head</strong> showed higher Sy
        values (up to 0.62), influenced by peat type and restoration status. For
        instance:
      </p>

      <ul>
        <li>Sy (Sharavogue, Raised Bog): 0.16 (±0.11)</li>
        <li>Sy (Liffey Head, Blanket Bog): 0.42–0.62 (±0.22)</li>
      </ul>

      <h3>Methodology Overview</h3>
      <ol>
        <li>
          Identify isolated rainfall events using local Met Éireann daily data.
        </li>
        <li>
          Measure water level change in dipwell loggers over 24h periods.
        </li>
        <li>
          Calculate Sy as rainfall divided by water level change (adjusted for
          evapotranspiration where applicable).
        </li>
      </ol>

      <p>
        This method provides a <strong>conservative estimate</strong> of Sy using
        publicly available rainfall data, enabling standardized comparisons across
        peatland sites. Fixed dipwell locations (from shapefiles) guide the selection
        of analysis points on the AI2Peat map.
      </p>

      <h3>Applications of VWB Analysis</h3>
      <ul>
        <li>
          Support restoration planning by identifying sites with low/high water storage
          capacity.
        </li>
        <li>
          Monitor hydrological change post-restoration.
        </li>
        <li>
          Quantify resilience of peatlands to climate-driven hydrological shifts.
        </li>
      </ul>
    </div>
  );
};

export default VWBAnalysisTab;